import InputLabeledContainer from 'tennders-common/form/components/new-inputs/InputLabeledContainer'
import InfiniteScroll from 'react-infinite-scroll-component'
import React, { useEffect, useRef, useState, memo } from 'react'
import TextInput from 'tennders-common/form/components/new-inputs/TextInput'
import { useDebounce } from 'use-debounce'
import { motion } from 'framer-motion'
import GLoading from 'tennders-market/common/components/element/GLoading'
import useTranslation from 'next-translate/useTranslation'
import styles from '../../styles/transactionSelect.module.scss'

type Props = {
  item: {
    id ?: number
  },
  onClick: (item: { id?: number}) => void,
  labelKey?: string
}

const SelectOption = memo(({ item, onClick, labelKey }: Props) => (
  <div onClick={() => onClick(item)} className={styles.item} key={item.id}>
    <div className={styles.itemTitle}>
      {item[labelKey]}
    </div>
  </div>
), (prev, next) => JSON.stringify(prev.item) === JSON.stringify(next.item))

const SearchableAsyncSelectLayout = ({ fetchCallback, onSelect, labelKey }) => {
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [debouncedValue] = useDebounce(search, 300)
  const page = useRef(1)
  const [hasMore, setHasMore] = useState(false)
  const loading = useRef(false)
  const [dataLoading, setDataLoading] = useState(false)
  const fetchNextPage = async () => {
    if (loading.current === true) {
      return
    }
    setDataLoading(true)
    loading.current = true
    page.current += 1
    const [data, hasNextPage] = await fetchCallback(page.current, search)
    setDataLoading(false)
    loading.current = false
    setItems(prev => [...prev, ...data])
    setHasMore(hasNextPage)
  }

  const fetchSearch = async () => {
    if (loading.current === true) {
      return
    }
    loading.current = true
    page.current = 1
    setDataLoading(true)
    const [data, hasNextPage] = await fetchCallback(1, debouncedValue)
    setItems([...data])
    setHasMore(hasNextPage)
    setDataLoading(false)
    loading.current = false
  }

  useEffect(() => {
    fetchSearch()
  }, [debouncedValue, fetchCallback])

  return (
    <motion.div transition={{ duration: 0.2 }} initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} id="scrollableDiv" className={styles.selectContainer}>
      <InputLabeledContainer
        name="search"
        form={{ search }}
        label={t('searchFilters:search_transaction')}
        onChange={(_, value) => setSearch(value)}
        renderInput={(props) => <TextInput {...props} />}
      />
      {dataLoading && search === '' ? (
        <div className={styles.loadignContainer}>
          <GLoading size={30} />
        </div>
      ) : null}
      <InfiniteScroll
        dataLength={items.length} // This is important field to render the next data
        next={fetchNextPage}
        hasMore={hasMore}
        scrollableTarget="scrollableDiv"
        loader={<h4>{t('searchFilters:loading')}</h4>}
        endMessage={!hasMore && items.length > 7 ? (
          <p className="text-center bold">{t('searchFilters:yay_you_have_seen')}</p>
        ) : null}
      >
        {items.map((item) => <SelectOption onClick={onSelect} key={item[labelKey]} labelKey={labelKey} item={item} />)}
      </InfiniteScroll>
    </motion.div>
  )
}

export default SearchableAsyncSelectLayout
